/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */

import React from 'react';
import { Provider } from 'react-redux';
import { persistStore } from 'redux-persist';

import { PersistGate } from 'redux-persist/integration/react';

import { getLocaleFromURI } from './src/helpers/location';
import { createStoreWithMiddleware as createStore, sagaMiddleware } from './src/state';
import { SEARCH_PARAMS_UPDATED, ROUTER_LOCATION_CHANGED } from './src/state/actionTypes';
import rootSaga from './src/state/sagas';
import { EVENTS } from './src/constants/analytics';
import {
  initAnalytics, trackEvent, trackPageView, trackScrollDepth,
} from './src/helpers/analytics';
import { dropInjectedScriptEvents } from './src/helpers/sentry';


const store = createStore();
const persistor = persistStore(store);

const transitionDelay = 250;

let locale;
let scrollCleanup = null;

initAnalytics();

export const wrapRootElement = ({ element }) => {
  const ConnectedRootElement = (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        {element}
      </PersistGate>
    </Provider>
  );
  return ConnectedRootElement;
};

export const onClientEntry = () => {
  require('whatwg-fetch'); // eslint-disable-line global-require
  sagaMiddleware.run(rootSaga);

  /* gatsby-plugin-sentry lazy-loads the SDK; this resolves to the same module
     (the plugin's own, hoisted, no nested copy), and addEventProcessor
     registers on the isolation scope, which does not depend on Sentry.init
     having run. Not a dependency of ours on purpose: the plugin owns the
     version, and a second copy would register on a scope nothing reads. */
  // eslint-disable-next-line import/no-extraneous-dependencies
  import('@sentry/browser')
    .then(({ addEventProcessor }) => addEventProcessor(dropInjectedScriptEvents))
    .catch(() => {});

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!link) return;
    const href = link.getAttribute('href') || '';
    /* The address itself is deliberately not sent. This listener matches any
       mailto:/tel: anywhere on the page, including anything an editor adds in
       Dato later, so forwarding the literal value would put an address into a
       GA4 custom parameter. That breaches Google's own PII policy whoever the
       address belongs to, and it would silently start shipping guest data the
       day someone renders a booking's own contact details as a link. The event
       plus page_location already answers which page a contact click came from. */
    if (href.startsWith('mailto:')) {
      trackEvent(EVENTS.CLICK_EMAIL, { contact_method: 'email' });
    } else if (href.startsWith('tel:')) {
      trackEvent(EVENTS.CLICK_PHONE, { contact_method: 'phone' });
    }
  });
};

// Read by the hydration probe in src/html.js. If this class is missing 8s
// after the document parses, the client bundle never took over and the
// server-rendered splash overlay is hidden so the visitor is not trapped.
export const onInitialClientRender = () => {
  document.documentElement.className += ' hydrated';
};

export const onRouteUpdate = ({ location }) => {
  const newLocale = getLocaleFromURI(location.pathname);
  if (locale !== newLocale) {
    store.dispatch({ type: SEARCH_PARAMS_UPDATED, payload: { locale: newLocale } });
    locale = newLocale;
  }
  store.dispatch({ type: ROUTER_LOCATION_CHANGED, payload: { location: location.pathname } });
  // One deferred call now covers both GA4 and the Meta PageView, which used to
  // run on its own 500ms timer.
  trackPageView();

  // Scroll depth: fire once per page when user scrolls past 200px
  if (scrollCleanup) scrollCleanup();
  const guardedHandler = () => {
    if (window.scrollY > 200) {
      trackScrollDepth();
      window.removeEventListener('scroll', guardedHandler);
      scrollCleanup = null;
    }
  };
  window.addEventListener('scroll', guardedHandler, { passive: true });
  scrollCleanup = () => window.removeEventListener('scroll', guardedHandler);
};

// https://github.com/gatsbyjs/gatsby/issues/5213#issuecomment-428368131
export const shouldUpdateScroll = ({ routerProps: { location }, getSavedScrollPosition }) => {
  if (location.action === 'POP') {
    const previousPosition = getSavedScrollPosition(location);
    window.setTimeout(
      () => window.scrollTo(...(previousPosition || [0, 0])),
      transitionDelay,
    );
  } else {
    window.setTimeout(() => window.scrollTo(0, 0), transitionDelay);
  }
  return false;
};
